import apiClient from './api';
import { toast } from 'sonner';

/**
 * After a master record is renamed, propagate the change to all downstream
 * records that reference the value. `masterType` must match a key in the
 * backend MASTER_REF_MAP.
 */
export const cascadeMasterUpdate = async (masterType, oldValue, newValue) => {
  if (!masterType || oldValue == null || newValue == null || oldValue === newValue) return;
  try {
    const res = await apiClient.post('/master-cascade/update', {
      master_type: masterType,
      old_value: String(oldValue),
      new_value: String(newValue),
    });
    const n = res.data?.updated ?? 0;
    if (n > 0) toast.success(`Updated ${n} record${n !== 1 ? 's' : ''} that referenced "${oldValue}"`);
  } catch (e) {
    console.warn('cascadeMasterUpdate failed:', e);
  }
};

/**
 * Two-step delete dialog:
 *   1. window.confirm("Delete this <masterType>?")
 *   2. window.confirm("Also remove this value from records where it's used?")
 * Then calls the master delete endpoint provided by the caller, and if the
 * user chose to cascade, POSTs to /master-cascade/delete to clear the field
 * from referencing records.
 *
 * Returns true when the master was deleted (regardless of cascade choice).
 */
export const confirmAndDeleteMaster = async ({
  masterType,
  valueLabel,
  deleteMasterFn,
}) => {
  const ok1 = window.confirm(`Delete this ${masterType.replace(/-/g, ' ')} entry?\n\n"${valueLabel}"`);
  if (!ok1) return false;
  const cascade = window.confirm(
    `Also remove "${valueLabel}" from records where it's currently used?\n\n` +
    `• OK — clear the value from every order / customer / BS order that references it.\n` +
    `• Cancel — keep the value on those records (they will show it as an orphan).`
  );
  try {
    await deleteMasterFn();
  } catch (e) {
    toast.error(e?.response?.data?.detail || `Failed to delete ${masterType}`);
    return false;
  }
  toast.success(`${masterType.replace(/-/g, ' ')} deleted`);
  try {
    const res = await apiClient.post('/master-cascade/delete', {
      master_type: masterType,
      value: String(valueLabel),
      delete_from_records: cascade,
    });
    const n = res.data?.cleared ?? 0;
    if (cascade && n > 0) toast.success(`Cleared "${valueLabel}" from ${n} record${n !== 1 ? 's' : ''}`);
  } catch (e) {
    console.warn('cascadeMasterDelete failed:', e);
  }
  return true;
};
